import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, MaskedLines } from "@/components/site/Motion";
import { IMAGES } from "@/data/products";

const Card = ({ to, testId, className, children }) => (
  <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} className={`h-full ${className}`}>
    <Link to={to} data-testid={testId} className="group relative flex flex-col h-full overflow-hidden rounded-[28px]">
      {children}
      <span className="absolute top-6 right-6 grid place-items-center w-11 h-11 rounded-full bg-white text-[#070B2E] transition-transform duration-500 group-hover:rotate-45 group-hover:scale-110 z-10">
        <ArrowUpRight className="w-5 h-5" />
      </span>
    </Link>
  </motion.div>
);

const Tag = ({ children, dark }) => (
  <span className={`self-start inline-block rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] ${dark ? "bg-white/10 text-[#7FE3EA]" : "bg-[#070B2E]/5 text-[#0B14A0]"}`}>{children}</span>
);

const SystemCard = () => (
  <Card to="/produkter/system" testId="product-card-redoc-system" className="min-h-[520px]">
    <img src={IMAGES.workspace} alt="Arbetsplats med dator" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#070B2E] via-[#070B2E]/60 to-[#070B2E]/10" />
    <div className="relative mt-auto p-8 sm:p-10 text-white">
      <Tag dark>01 · System</Tag>
      <h3 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-5 leading-[1.02]">Redoc &<br />Redoc AML & KYC</h3>
      <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <p className="text-[#D5D8F0] leading-relaxed"><strong className="text-white">Redoc</strong> – fokus på kvalitetssäkring av uppdragen.</p>
        <p className="text-[#D5D8F0] leading-relaxed"><strong className="text-white">Redoc AML & KYC</strong> – fokus på AML och KYC.</p>
      </div>
    </div>
  </Card>
);

const AmlCard = () => (
  <Card to="/produkter/aml-kyc" testId="product-card-aml-kyc" className="min-h-[250px]">
    <div className="absolute inset-0 bg-white border border-[#E3E2DA] rounded-[28px]" />
    <div className="relative p-8 flex flex-col h-full">
      <Tag>02 · amlkyc.nu</Tag>
      <h3 className="font-display text-3xl font-bold tracking-tight mt-auto pt-10 text-[#070B2E]">AML & KYC</h3>
      <p className="mt-3 text-[#3A3F5C] leading-relaxed">Kundkännedom, riskanalyser och allmän riskbedömning enligt Penningtvättlagen.</p>
    </div>
  </Card>
);

const ReconCard = () => (
  <Card to="/produkter/recon-ai" testId="product-card-recon-ai" className="min-h-[250px]">
    <div className="absolute inset-0 bg-[#0B14A0]" />
    <div aria-hidden="true" className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full border-[28px] border-[#19C3CF]/40 transition-transform duration-1000 group-hover:scale-125" />
    <div className="relative p-8 flex flex-col h-full text-white">
      <Tag dark>03 · recon.redoc.se</Tag>
      <h3 className="font-display text-3xl font-bold tracking-tight mt-auto pt-10">Recon.ai</h3>
      <p className="mt-3 text-[#D5D8F0] leading-relaxed max-w-xs">Avstämning av kontoutdrag med AI – på sekunder, utan lagring.</p>
    </div>
  </Card>
);

export const ProductBento = () => (
  <section id="produkter" className="relative py-24 lg:py-36" data-testid="products-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
        <div className="lg:col-span-7">
          <Eyebrow>Produkter</Eyebrow>
          <MaskedLines as="h2" inView className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1] mt-4" lines={["Webbaserade verktyg", "för en tryggare byrå."]} />
        </div>
        <Reveal className="lg:col-span-5" delay={0.15}>
          <p className="text-base sm:text-lg text-[#3A3F5C] leading-relaxed">Allt du behöver för att kvalitetssäkra uppdragen, uppfylla Penningtvättlagen och stämma av kontoutdrag – lika enkelt på mobilen som på datorn.</p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-2 gap-5">
        <Reveal className="lg:col-span-7 lg:row-span-2"><SystemCard /></Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}><AmlCard /></Reveal>
        <Reveal className="lg:col-span-5" delay={0.2}><ReconCard /></Reveal>
      </div>
    </div>
  </section>
);
